import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, Sparkles, ClipboardCheck, CalendarDays, Inbox, Radar, Settings, Sun, Moon, LogOut, Menu, X, ShieldCheck, Share2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { api } from "../lib/api";
import { hexToHsl, contrastFg } from "../lib/colors";
import { formatBuildTime } from "../lib/buildInfo";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/brand", label: "Brand Knowledge", icon: BookOpen, id: "nav-brand" },
  { to: "/generator", label: "AI Post Generator", icon: Sparkles, id: "nav-generator" },
  { to: "/review", label: "Review Feed", icon: ClipboardCheck, id: "nav-review" },
  { to: "/calendar", label: "Content Calendar", icon: CalendarDays, id: "nav-calendar" },
  { to: "/inbox", label: "Unified Inbox", icon: Inbox, id: "nav-inbox" },
  { to: "/competitors", label: "Competitor Intel", icon: Radar, id: "nav-competitors" },
  { to: "/denzel-share", label: "Denzel Share Tray", icon: Share2, id: "nav-denzel-share" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { brands, selected, setSelected } = useBrand();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") !== "light");
  const [open, setOpen] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const [denzelPending, setDenzelPending] = useState(0);
  const [env, setEnv] = useState(null);

  useEffect(() => {
    const check = () => {
      api.get("/posts/publish-failures").then((r) => setFailCount(r.data.count)).catch(() => {});
      api.get("/denzel/share-tray?status=pending").then((r) => setDenzelPending((r.data.counts?.pending) || 0)).catch(() => {});
    };
    check();
    api.get("/environment").then((r) => setEnv(r.data)).catch(() => {});
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const activeBrand = brands.find((b) => b.name === selected);
  const themeVars = activeBrand?.color
    ? {
        "--accent": hexToHsl(activeBrand.color),
        "--accent-foreground": contrastFg(activeBrand.color),
        "--ring": hexToHsl(activeBrand.color),
        "--accent-2": hexToHsl(activeBrand.secondary_color || activeBrand.color),
        "--accent-2-foreground": contrastFg(activeBrand.secondary_color || activeBrand.color),
      }
    : {};

  const published = formatBuildTime();

  return (
    <div className="min-h-screen bg-background grain" style={themeVars}>
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
          <img src="/logo.png" alt="logo" className="w-10 h-10 rounded-full" />
          <div className="min-w-0">
            <p className="font-heading font-semibold text-base leading-tight">Harmony Wellness Group</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <p className="text-xs text-muted-foreground tracking-wide uppercase">Social Media Manager</p>
              {env && (
                <span
                  data-testid="env-badge"
                  className="text-[9px] font-bold tracking-[0.15em] uppercase px-1.5 py-0.5 rounded"
                  style={{ background: env.bg, color: env.color }}
                  title={`This is the ${env.name} environment`}
                >
                  {env.label}
                </span>
              )}
            </div>
            {published && (
              <p data-testid="sidebar-build-timestamp" className="text-[10px] text-muted-foreground/70 mt-1">
                Published {published}
              </p>
            )}
          </div>
        </div>
        <div className="px-3 pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold px-2 mb-2">Brand workspace</p>
          <Select value={selected || "__all__"} onValueChange={(v) => setSelected(v === "__all__" ? "" : v)}>
            <SelectTrigger className={`w-full ${selected ? "border-accent" : ""}`} data-testid="brand-switcher" style={activeBrand?.color ? { borderColor: activeBrand.color } : {}}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="brand-option-all">
                <span className="flex items-center gap-2"><img src="/logo.png" alt="" className="w-3.5 h-3.5 rounded-full" /> HWG · All brands</span>
              </SelectItem>
              {brands.map((b) => (
                <SelectItem key={b.id} value={b.name} data-testid={`brand-option-${b.id}`}>
                  <span className="flex items-center gap-2">
                    {b.logo_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`} alt="" className="w-3.5 h-3.5 rounded-full object-cover shrink-0" />
                    ) : (
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: b.color || "hsl(var(--accent))" }} />
                    )}
                    {b.name}{b.status === "draft" ? " · draft" : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.id} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <n.icon size={18} strokeWidth={1.8} />
              {n.label}
              {n.to === "/admin" && failCount > 0 && (
                <span data-testid="publish-failure-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold flex items-center justify-center" title={`${failCount} publish failure(s) — see Publish log`}>
                  {failCount}
                </span>
              )}
              {n.to === "/denzel-share" && denzelPending > 0 && (
                <span data-testid="denzel-pending-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-[11px] font-bold flex items-center justify-center" title={`${denzelPending} post${denzelPending !== 1 ? "s" : ""} waiting for Denzel to reshare`}>
                  {denzelPending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} aria-label="Log out">
              <LogOut size={16} />
            </Button>
          </div>
          <Button variant="outline" size="sm" className="w-full" data-testid="theme-toggle" onClick={() => setDark(!dark)}>
            {dark ? <Sun size={14} className="mr-2" /> : <Moon size={14} className="mr-2" />}
            {dark ? "Light mode" : "Dark mode"}
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-background/85 backdrop-blur-xl border-b border-border">
          <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </Button>
          <img src="/logo.png" alt="" className="w-7 h-7 rounded-full" />
          <span className="font-heading font-semibold">Harmony Wellness Group</span>
        </header>
        <main className="relative z-10 p-4 sm:p-6 lg:p-10 max-w-[1500px]">{children}</main>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
